"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { InlineCombobox } from "@/components/ui/inline-combobox";
import {
  StandaloneAddressAutocomplete,
  type ParsedAddress,
} from "@/components/inputs/address-autocomplete";
import type { DeliveryLocation } from "@/lib/checkout/delivery-location";
import type { SavedAddressOption } from "@/lib/checkout/saved-address-options";
import { cn } from "@/lib/utils";

export type { SavedAddressOption } from "@/lib/checkout/saved-address-options";

const US_REGION_CODES = ["us"];
const ERROR_SETTLE_MS = 1200;
const LOOKUP_UNAVAILABLE_HINT =
  "Address suggestions are unavailable. Type the street, city, state, and 5-digit ZIP.";

interface DeliveryLocationFieldProps {
  savedAddressOptions: SavedAddressOption[];
  location: DeliveryLocation | null;
  addressDisplay: string;
  onSelectSaved: (location: DeliveryLocation) => void;
  onAddressChange: (display: string, parsed: ParsedAddress | null) => void;
  onResolvingChange?: (resolving: boolean) => void;
  inputId?: string;
  label?: string;
  errorMessage?: string;
  saveNotice?: string;
  forceError?: boolean;
  compact?: boolean;
}

type DeliveryAddressModeProps = Pick<
  DeliveryLocationFieldProps,
  | "savedAddressOptions"
  | "location"
  | "addressDisplay"
  | "onSelectSaved"
  | "onAddressChange"
>;

function useDeliveryAddressMode({
  savedAddressOptions,
  location,
  addressDisplay,
  onSelectSaved,
  onAddressChange,
}: DeliveryAddressModeProps) {
  const [enteringNew, setEnteringNew] = useState(false);
  const [touched, setTouched] = useState(false);
  const hasSaved = savedAddressOptions.length > 0;
  const matchedSaved = savedAddressOptions.find(
    (option) => option.id === location?.savedAddressId,
  );
  const hasUnmatchedAddress = !matchedSaved && (!!location || !!addressDisplay);
  const showNewAddress =
    !hasSaved || (!matchedSaved && (enteringNew || hasUnmatchedAddress));

  const chooseSaved = (option: SavedAddressOption) => {
    setEnteringNew(false);
    setTouched(false);
    onSelectSaved(option.location);
  };
  return {
    hasSaved,
    matchedSaved,
    showNewAddress,
    enteringNew,
    touched,
    markTouched: () => setTouched(true),
    chooseSaved,
    startNewAddress: () => {
      setEnteringNew(true);
      setTouched(false);
      onAddressChange("", null);
    },
    backToSaved: () => {
      const preferred =
        savedAddressOptions.find((option) => option.isDefault) ??
        savedAddressOptions[0];
      if (preferred) chooseSaved(preferred);
    },
  };
}

function useErrorAfterSettling(
  errorMessage: string | undefined,
  value: string,
  active: boolean,
): boolean {
  const [settledValue, setSettledValue] = useState<string | null>(null);
  useEffect(() => {
    if (!errorMessage || !active) return;
    const timer = setTimeout(() => setSettledValue(value), ERROR_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [errorMessage, value, active]);
  return !!errorMessage && active && settledValue === value;
}

function useFocusPickerAfterFlip(showNewAddress: boolean, pickerId: string) {
  const wasNewAddressRef = useRef(showNewAddress);
  useEffect(() => {
    const flippedToPicker = wasNewAddressRef.current && !showNewAddress;
    wasNewAddressRef.current = showNewAddress;
    if (flippedToPicker && document.activeElement === document.body) {
      document.getElementById(pickerId)?.focus();
    }
  }, [showNewAddress, pickerId]);
}

function useFieldIds(
  props: DeliveryLocationFieldProps,
  mode: ReturnType<typeof useDeliveryAddressMode>,
) {
  const inputId = props.inputId ?? "delivery-address";
  const newAddressId = mode.hasSaved ? `${inputId}-new` : inputId;
  const settled = useErrorAfterSettling(
    props.errorMessage,
    props.addressDisplay,
    mode.showNewAddress,
  );
  useFocusPickerAfterFlip(mode.showNewAddress, inputId);
  return {
    inputId,
    newAddressId,
    errorId: `${newAddressId}-error`,
    showError:
      mode.showNewAddress &&
      (mode.touched || settled || !!props.forceError) &&
      !!props.errorMessage,
  };
}

interface DeliveryAddressInputProps {
  mode: ReturnType<typeof useDeliveryAddressMode>;
  inputId: string;
  newAddressId: string;
  errorId?: string;
  savedAddressOptions: SavedAddressOption[];
  addressDisplay: string;
  onAddressChange: DeliveryLocationFieldProps["onAddressChange"];
  onResolvingChange?: DeliveryLocationFieldProps["onResolvingChange"];
  compact: boolean;
}

function DeliveryAddressInput({
  mode,
  inputId,
  newAddressId,
  errorId,
  savedAddressOptions,
  addressDisplay,
  onAddressChange,
  onResolvingChange,
  compact,
}: DeliveryAddressInputProps) {
  if (mode.showNewAddress) {
    return (
      <NewAddressEntry
        id={newAddressId}
        value={addressDisplay}
        autoFocus={mode.enteringNew}
        onChange={onAddressChange}
        onBlur={mode.markTouched}
        onResolvingChange={onResolvingChange}
        errorId={errorId}
        onBackToSaved={mode.hasSaved ? mode.backToSaved : undefined}
        compact={compact}
      />
    );
  }
  return (
    <SavedAddressCombobox
      id={inputId}
      options={savedAddressOptions}
      selectedId={mode.matchedSaved?.id ?? null}
      onAddNew={mode.startNewAddress}
      onSelectSaved={mode.chooseSaved}
      compact={compact}
    />
  );
}

export function DeliveryLocationField(props: DeliveryLocationFieldProps) {
  const {
    savedAddressOptions,
    addressDisplay,
    onAddressChange,
    onResolvingChange,
    label = "Delivery address",
    errorMessage,
    saveNotice,
    compact = true,
  } = props;
  const mode = useDeliveryAddressMode(props);
  const { inputId, newAddressId, errorId, showError } = useFieldIds(
    props,
    mode,
  );

  return (
    <div className="space-y-1.5">
      <label
        htmlFor={mode.showNewAddress ? newAddressId : inputId}
        className="block text-xs font-medium text-foreground"
      >
        {label}
      </label>
      <DeliveryAddressInput
        mode={mode}
        inputId={inputId}
        newAddressId={newAddressId}
        errorId={showError ? errorId : undefined}
        savedAddressOptions={savedAddressOptions}
        addressDisplay={addressDisplay}
        onAddressChange={onAddressChange}
        onResolvingChange={onResolvingChange}
        compact={compact}
      />
      <FieldMessages
        errorId={errorId}
        errorMessage={showError ? errorMessage : undefined}
        saveNotice={saveNotice}
      />
    </div>
  );
}

function FieldMessages({
  errorId,
  errorMessage,
  saveNotice,
}: {
  errorId: string;
  errorMessage?: string;
  saveNotice?: string;
}) {
  return (
    <>
      {errorMessage ? (
        <p id={errorId} role="alert" className="text-xs text-destructive-strong">
          {errorMessage}
        </p>
      ) : null}
      {saveNotice ? (
        <p role="status" className="text-xs text-muted-foreground">
          {saveNotice}
        </p>
      ) : null}
    </>
  );
}

interface NewAddressEntryProps {
  id: string;
  value: string;
  autoFocus: boolean;
  onChange: (display: string, parsed: ParsedAddress | null) => void;
  onBlur: () => void;
  onResolvingChange?: (resolving: boolean) => void;
  errorId?: string;
  onBackToSaved?: () => void;
  compact: boolean;
}

function NewAddressEntry({ id, value, autoFocus, onChange, onBlur, onResolvingChange, errorId, onBackToSaved, compact }: NewAddressEntryProps) {
  return (
    <div className={cn("space-y-1", onBackToSaved && "space-y-1.5")}>
      <StandaloneAddressAutocomplete
        id={id}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onResolvingChange={onResolvingChange}
        placeholder="Street address, city, or ZIP"
        regionCodes={US_REGION_CODES}
        unavailableHint={LOOKUP_UNAVAILABLE_HINT}
        compact={compact}
        autoFocus={autoFocus}
        aria-invalid={!!errorId}
        aria-describedby={errorId}
      />
      {onBackToSaved ? (
        <Button
          type="button"
          variant="link"
          size="bare"
          className="text-xs"
          onClick={onBackToSaved}
        >
          Use a saved address instead
        </Button>
      ) : null}
    </div>
  );
}

function SavedAddressCombobox({
  id,
  options,
  selectedId,
  onAddNew,
  onSelectSaved,
  compact,
}: {
  id: string;
  compact: boolean;
  options: SavedAddressOption[];
  selectedId: string | null;
  onAddNew: () => void;
  onSelectSaved: (option: SavedAddressOption) => void;
}) {
  return (
    <InlineCombobox
      id={id}
      ariaLabel="Deliver to"
      value={selectedId}
      options={options.map((option) => ({
        value: option.id,
        label: option.label,
      }))}
      onSelect={(value) => {
        const option = options.find((candidate) => candidate.id === value);
        if (option) onSelectSaved(option);
      }}
      action={{ label: "Add a new address", onSelect: onAddNew }}
      placeholder="Choose an address"
      searchPlaceholder="Search saved addresses"
      className="text-sm"
      size={compact ? "sm" : "default"}
    />
  );
}
